import type { LocalMediaRecord } from './kieu';

type MediaResponse<T> = {
  source?: string;
  type?: string;
  requestId?: string;
  ok?: boolean;
  data?: T;
  error?: string;
  loaded?: number;
  total?: number;
};

function request<T>(
  tenantId: string,
  action: string,
  payload: Record<string, unknown>,
  onProgress?: (loaded: number, total: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const requestId = 'media_' + crypto.randomUUID();
    const timer = window.setTimeout(() => finish(new Error('MEDIA_VAULT_TIMEOUT')), 120000);
    function finish(error?: Error, data?: T) {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      if (error) reject(error); else resolve(data as T);
    }
    function onMessage(event: MessageEvent<MediaResponse<T>>) {
      if (event.origin !== window.location.origin) return;
      const message = event.data;
      if (message?.source !== 'FBMAX_MEDIA_RES' || message.requestId !== requestId) return;
      if (message.type === 'PROGRESS') {
        onProgress?.(Number(message.loaded || 0), Number(message.total || 0));
        return;
      }
      if (message.type === 'RESULT') {
        if (message.ok) finish(undefined, message.data);
        else finish(new Error(message.error || 'MEDIA_VAULT_FAILED'));
      }
    }
    window.addEventListener('message', onMessage);
    window.postMessage({ source: 'FBMAX_MEDIA_DASHBOARD', requestId, tenantId, action, ...payload }, window.location.origin);
  });
}

export const mediaVault = {
  importFile: (tenantId: string, file: File, onProgress?: (loaded: number, total: number) => void) =>
    request<LocalMediaRecord>(tenantId, 'MEDIA_VAULT_IMPORT', { file }, onProgress),
  status: (tenantId: string, localMediaIds: string[]) =>
    request<Array<LocalMediaRecord | { localMediaId: string; status: 'MISSING' }>>(tenantId, 'MEDIA_VAULT_STATUS', { localMediaIds }),
  preview: (tenantId: string, localMediaId: string) =>
    request<{ record: LocalMediaRecord; blob: Blob }>(tenantId, 'MEDIA_VAULT_PREVIEW', { localMediaId }),
  remove: (tenantId: string, localMediaId: string) =>
    request<{ localMediaId: string; deleted: boolean }>(tenantId, 'MEDIA_VAULT_DELETE', { localMediaId }),
};
